import React, { useState, useEffect } from 'react';
import * as api from '../../utils/api';
import styles from './BankPage.module.css';
import { useAuth } from '../../context/AuthContext';
import { useProgressBar } from '../../context/ProgressBarContext';
import RegistrationSteps from '../../components/RegistrationSteps';
import MessageBox from '../../components/MessageBox';
import { useNavigate } from 'react-router-dom';

const indianBanks = [
  'State Bank of India',
  'HDFC Bank',
  'ICICI Bank',
  'Kotak Mahindra Bank',
  'Axis Bank',
  'Punjab National Bank',
  'Bank of Baroda',
  'IndusInd Bank',
  'Union Bank of India',
  'Canara Bank',
  'Yes Bank',
];

function BankPage() {
  const [formData, setFormData] = useState({
    bankName: '',
    accountNumber: '',
    reEnteredAccountNumber: '',
    ifscCode: '',
  });
  const [errors, setErrors] = useState({});
  const [suggestions, setSuggestions] = useState([]);
  const [isSuggestionsVisible, setIsSuggestionsVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const { showProgressBar, updateProgressBar, hideProgressBar } = useProgressBar();

  useEffect(() => {
    if (user) {
      setFormData({
        bankName: user.bankName || '',
        accountNumber: user.accountNumber || '',
        reEnteredAccountNumber: '',
        ifscCode: user.ifscCode || '',
      });
    }
  }, [user]);

  const { bankName, accountNumber, reEnteredAccountNumber, ifscCode } = formData;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === 'bankName') {
      if (value) {
        const filteredSuggestions = indianBanks.filter(bank =>
          bank.toLowerCase().includes(value.toLowerCase())
        );
        setSuggestions(filteredSuggestions);
        setIsSuggestionsVisible(true);
      } else {
        setSuggestions([]);
        setIsSuggestionsVisible(false);
      }
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setFormData({ ...formData, bankName: suggestion });
    setSuggestions([]);
    setIsSuggestionsVisible(false);
  };

  const validate = () => {
    let tempErrors = {};
    if (!bankName) tempErrors.bankName = 'Bank name is required.';
    if (!accountNumber.match(/^[0-9]{9,18}$/)) {
      tempErrors.accountNumber = 'Account number must be between 9 and 18 digits.';
    }
    if (accountNumber !== reEnteredAccountNumber) {
      tempErrors.reEnteredAccountNumber = 'Account numbers do not match.';
    }
    if (!ifscCode.match(/^[A-Z]{4}0[A-Z0-9]{6}$/)) {
      tempErrors.ifscCode = 'IFSC code is not valid.';
    }
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage(null);
    if (validate()) {
      showProgressBar();
      updateProgressBar(30);
      const bankDetails = {
        bankName,
        accountNumber,
        ifscCode,
      };

      try {
        await new Promise(resolve => setTimeout(resolve, 250));
        updateProgressBar(60);
        const { data: updatedUser } = await api.updateUserBankDetails(bankDetails);
        updateUser(updatedUser);
        updateProgressBar(90);
        await new Promise(resolve => setTimeout(resolve, 250));
        navigate('/account-status');
      } catch (err) {
        setErrorMessage(err.response?.data?.msg || 'An unexpected error occurred. Please try again.');
      } finally {
        hideProgressBar();
      }
    }
  };

  return (
    <div className={styles.bankPageWrapper}>
      {errorMessage && <MessageBox message={errorMessage} type="error" onClose={() => setErrorMessage(null)} />}

      <RegistrationSteps currentStep={4} />

      <div className={styles.bankContainer}>
        <div className={styles.bankFormSection}>
          <div className={styles.formHeader}>
            <h2>Bank Details</h2>
            <p>Step 4: Final Step</p>
          </div>

          <form onSubmit={onSubmit} autoComplete="off">
            <div className={styles.formGroup}>
              <label htmlFor="bankName">Bank Name</label>
              <div className={styles.inputWithSuggestions}>
                <input
                  type="text"
                  id="bankName"
                  name="bankName"
                  value={bankName}
                  onChange={handleChange}
                  placeholder="Enter bank name"
                />
                {isSuggestionsVisible && suggestions.length > 0 && (
                  <ul className={styles.suggestionsList}>
                    {suggestions.map((suggestion, index) => (
                      <li key={index} onClick={() => handleSuggestionClick(suggestion)}>
                        {suggestion}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {errors.bankName && <p className={styles.errorText}>{errors.bankName}</p>}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="accountNumber">Account Number</label>
              <input
                type="text"
                id="accountNumber"
                name="accountNumber"
                value={accountNumber}
                onChange={handleChange}
                placeholder="Enter account number"
              />
              {errors.accountNumber && <p className={styles.errorText}>{errors.accountNumber}</p>}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="reEnteredAccountNumber">Re-enter Account Number</label>
              <input
                type="text"
                id="reEnteredAccountNumber"
                name="reEnteredAccountNumber"
                value={reEnteredAccountNumber}
                onChange={handleChange}
                placeholder="Re-enter account number"
              />
              {errors.reEnteredAccountNumber && <p className={styles.errorText}>{errors.reEnteredAccountNumber}</p>}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="ifscCode">IFSC Code</label>
              <input
                type="text"
                id="ifscCode"
                name="ifscCode"
                value={ifscCode}
                onChange={handleChange}
                placeholder="Enter IFSC code"
              />
              {errors.ifscCode && <p className={styles.errorText}>{errors.ifscCode}</p>}
            </div>

            <button type="submit" className={styles.btnSubmit}>Complete Registration</button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default BankPage;